import React, { useState, useEffect, useRef } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth, useProject } from "../App";
import axios from "axios";
import { 
  Building2, LayoutDashboard, Users, CheckSquare, Calendar, 
  Sparkles, BarChart3, Settings, LogOut, Menu, X, ChevronDown, ChevronRight, Brain, Crown,
  Webhook, Footprints, Megaphone, UsersRound, GitBranch, ScrollText, AlertTriangle, TrendingUp, Heart, Workflow,
  Scale, Calculator, MessageSquare, Radar, Bot, Headset, BrainCircuit,
  Warehouse, Handshake, Receipt, ShieldCheck, Gauge, BookOpen, TrendingDown, Briefcase, Shield, Bell, Check, Trash2, Zap, Inbox,
  Percent, FileText, ClipboardSignature, UserPlus, Globe, Activity, Settings2, ParkingCircle, RefreshCw,
  BarChart, GraduationCap, Upload, Home, KeyRound, Wrench
} from "lucide-react";
import { Button } from "./ui/button";
import { Sheet, SheetContent, SheetTrigger } from "./ui/sheet";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "./ui/dropdown-menu";
import { Badge } from "./ui/badge";
import InstallPrompt from "./InstallPrompt";
import MobileBottomNav from "./MobileBottomNav";
import { API } from "../App";

const NavLink = ({ item, onClick, currentPath }) => {
  const isActive = currentPath === item.path;
  const Icon = item.icon;
  
  return (
    <Link
      to={item.path}
      onClick={onClick}
      data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
      className={`flex items-center gap-3 px-3 py-1.5 rounded-lg transition-all duration-200 text-[13px] ${
        isActive 
          ? "bg-white/15 text-white" 
          : "text-slate-300 hover:text-white hover:bg-white/10"
      }`}
    >
      <Icon className="w-3.5 h-3.5" />
      <span className="font-medium">{item.label}</span>
      {item.badge && (
        <span className="ml-auto text-[10px] bg-amber-500 text-white px-1.5 py-0.5 rounded-full leading-none">{item.badge}</span>
      )}
    </Link>
  );
};

const NavSection = ({ group, openGroup, setOpenGroup, currentPath }) => {
  const hasActivePath = group.items.some(item => currentPath === item.path);
  const btnRef = React.useRef(null);
  const flyoutTopRef = React.useRef(0);
  const isOpen = openGroup === group.label;

  const handleToggle = () => {
    if (!isOpen && btnRef.current) {
      const rect = btnRef.current.getBoundingClientRect();
      const maxTop = window.innerHeight - group.items.length * 36 - 40;
      flyoutTopRef.current = Math.min(rect.top, maxTop);
    }
    setOpenGroup(isOpen ? null : group.label);
  };

  return (
    <div ref={btnRef} data-testid={`nav-group-${group.label.toLowerCase().replace(/\s+/g, '-')}`} className="relative">
      <button
        onClick={handleToggle}
        className={`w-full flex items-center justify-between px-3 py-1.5 text-[11px] font-semibold uppercase tracking-wider transition-colors rounded-md ${
          isOpen ? "text-amber-400 bg-white/5" : hasActivePath ? "text-slate-300" : "text-slate-500 hover:text-slate-300"
        }`}
      >
        {group.label}
        <ChevronRight className={`w-3 h-3 transition-transform duration-200 ${isOpen ? "rotate-0" : ""}`} />
      </button>

      {isOpen && (
        <div
          className="fixed left-64 z-[100] w-52 bg-slate-800 border border-slate-700 rounded-lg shadow-xl py-1.5 max-h-[70vh] overflow-y-auto"
          data-flyout="true"
          style={{ top: flyoutTopRef.current }}
          onMouseDown={(e) => e.stopPropagation()}
        >
          <p className="px-3 py-1 text-[10px] font-semibold uppercase tracking-wider text-slate-500 border-b border-slate-700 mb-1">{group.label}</p>
          {group.items.map(item => (
            <NavLink key={item.path} item={item} currentPath={currentPath} onClick={() => setOpenGroup(null)} />
          ))}
        </div>
      )}
    </div>
  );
};

const ROLE_LABELS = { admin: 'Admin', ceo: 'CEO', hod_sales: 'HOD Sales', sales_head: 'Sales Head', sales_rep: 'Sales Rep', telecaller: 'Telecaller', marketing_manager: 'Marketing Manager', sales_manager: 'Sales Manager' };

const Layout = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [moreOpen, setMoreOpen] = useState(false);
  const [openGroup, setOpenGroup] = useState(null);
  const [flyoutPos, setFlyoutPos] = useState(0);
  const sidebarRef = React.useRef(null);
  const [pendingCount, setPendingCount] = useState(0);
  const [userPerms, setUserPerms] = useState(null);
  
  // Notification bell state
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [bellOpen, setBellOpen] = useState(false);
  const bellRef = useRef(null);
  const mobilePanelRef = useRef(null);
  const { projects, selectedProjectId, selectProject } = useProject();

  const isManager = ['admin', 'ceo', 'hod_sales', 'sales_head', 'marketing_manager'].includes(user?.role);
  const isAdmin = ['admin', 'ceo'].includes(user?.role);

  // Fetch notifications for bell icon
  const fetchNotifications = async () => {
    if (!user) return;
    try {
      const res = await axios.get(`${API}/notifications/bell`);
      setNotifications(res.data.notifications || []);
      setUnreadCount(res.data.unread_count || 0);
    } catch { /* ignore */ }
  };

  // Poll for notifications every 30 seconds
  useEffect(() => {
    if (!user) return;
    fetchNotifications();
    const interval = setInterval(fetchNotifications, 30000);
    return () => clearInterval(interval);
  }, [user]);

  // Close bell dropdown when clicking outside (desktop only)
  useEffect(() => {
    const handleClickOutside = (e) => {
      // Check if click is inside desktop bell area
      const isInsideDesktopBell = bellRef.current && bellRef.current.contains(e.target);
      // Check if click is inside mobile panel
      const isInsideMobilePanel = mobilePanelRef.current && mobilePanelRef.current.contains(e.target);
      
      // Only close if click is outside both
      if (!isInsideDesktopBell && !isInsideMobilePanel) {
        setBellOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const markAsRead = async (notificationId) => {
    try {
      await axios.put(`${API}/notifications/bell/${notificationId}/read`);
      setNotifications(prev => prev.map(n => 
        n.notification_id === notificationId ? { ...n, is_read: true } : n
      ));
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch { /* ignore */ }
  };

  const markAllAsRead = async () => {
    try {
      await axios.put(`${API}/notifications/bell/read-all`);
      setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
      setUnreadCount(0);
    } catch { /* ignore */ }
  };

  const clearAllNotifications = async () => {
    try {
      const res = await axios.delete(`${API}/notifications/bell/clear-all`);
      setNotifications([]);
      setUnreadCount(0);
      // Keep panel open briefly to show empty state, then close
      setTimeout(() => setBellOpen(false), 800);
    } catch (e) { 
      console.error("Clear all error:", e.response?.data || e.message);
      alert("Failed to clear notifications: " + (e.response?.data?.detail || e.message));
    }
  };

  const handleNotificationClick = (e, notification) => {
    e.stopPropagation();
    e.preventDefault();
    
    if (!notification.is_read) {
      markAsRead(notification.notification_id);
    }
    // Only navigate if link exists and is not empty
    if (notification.link && notification.link.trim() !== '') {
      setBellOpen(false);
      navigate(notification.link);
    }
  };

  const getNotificationIcon = (category) => {
    const icons = {
      lead: <Users className="w-4 h-4 text-blue-500" />,
      task: <CheckSquare className="w-4 h-4 text-amber-500" />,
      visit: <Calendar className="w-4 h-4 text-green-500" />,
      approval: <ShieldCheck className="w-4 h-4 text-purple-500" />,
      deal_room: <Briefcase className="w-4 h-4 text-rose-500" />,
    };
    return icons[category] || <Bell className="w-4 h-4 text-slate-500" />;
  };

  const formatTimeAgo = (dateStr) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diff = Math.floor((now - date) / 1000);
    if (diff < 60) return "Just now";
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    if (diff < 604800) return `${Math.floor(diff / 86400)}d ago`;
    return date.toLocaleDateString();
  };

  // Fetch pending user count for admin
  useEffect(() => {
    if (!isAdmin) return;
    const fetchPending = async () => {
      try {
        const res = await axios.get(`${API}/governance/users/pending`);
        setPendingCount(Array.isArray(res.data) ? res.data.length : 0);
      } catch { /* ignore */ }
    };
    fetchPending();
    const interval = setInterval(fetchPending, 30000);
    return () => clearInterval(interval);
  }, [isAdmin]);

  // Fetch user permissions
  useEffect(() => {
    if (!user) return;
    axios.get(`${API}/role-matrix/my-permissions`).then(res => {
      setUserPerms(res.data.permissions);
    }).catch(() => {});
  }, [user]);

  const hasAccess = (moduleKey) => {
    if (!moduleKey) return true; // no key = always show
    if (isAdmin) return true;
    if (!userPerms) return true; // still loading, show all
    return userPerms[moduleKey] === true;
  };

  const allNavGroups = [
    {
      label: "Core",
      items: [
        { path: "/dashboard", label: "Dashboard", icon: LayoutDashboard, moduleKey: "dashboard" },
        { path: "/manager-dashboard", label: "Manager Hub", icon: Gauge, moduleKey: "manager_dashboard" },
        { path: "/leads", label: "Leads", icon: Users, moduleKey: "leads" },
        { path: "/lead-inbox", label: "Lead Inbox", icon: Inbox, badge: "New", moduleKey: "lead_inbox" },
        { path: "/tasks", label: "Tasks", icon: CheckSquare, moduleKey: "tasks" },
        { path: "/site-visits", label: "Site Visits", icon: Calendar, moduleKey: "site_visits" },
      ],
    },
    {
      label: "AI Tools",
      items: [
        { path: "/copilot", label: "AI Copilot", icon: Sparkles, badge: "AI", moduleKey: "ai_copilot" },
        { path: "/management", label: "Management", icon: Crown, moduleKey: "ai_management" },
        { path: "/ai-training", label: "AI Training", icon: BookOpen, moduleKey: "ai_training" },
        { path: "/ai-learning", label: "AI Learning", icon: Brain, badge: "New", moduleKey: "ai_learning" },
        { path: "/chatbot", label: "Chatbot", icon: Bot, moduleKey: "chatbot" },
        { path: "/telecaller", label: "Telecaller Queue", icon: Headset, moduleKey: "telecaller" },
      ],
    },
    {
      label: "Sales",
      items: [
        { path: "/deal-rooms", label: "Deal Rooms", icon: Briefcase, moduleKey: "deal_rooms" },
        { path: "/objection-handler", label: "Objections", icon: MessageSquare, moduleKey: "objection_handler" },
        { path: "/negotiations", label: "Negotiations", icon: Handshake, moduleKey: "negotiations" },
        { path: "/queues", label: "Lead Queues", icon: Users, badge: "New", moduleKey: "lead_queues" },
        { path: "/discount-approvals", label: "Discount Approvals", icon: Percent, moduleKey: "discount_approvals" },
        { path: "/eoi", label: "EOI (Pre-Launch)", icon: ClipboardSignature, moduleKey: "eoi" },
        { path: "/bookings", label: "Bookings", icon: FileText, moduleKey: "bookings" },
        { path: "/referrals", label: "Referrals", icon: UserPlus, moduleKey: "referrals" },
        { path: "/walk-in", label: "Walk-in Entry", icon: Footprints, moduleKey: "walk_in" },
        { path: "/project-compare", label: "Compare Projects", icon: Scale, moduleKey: "project_compare" },
        { path: "/emi-calculator", label: "EMI Calculator", icon: Calculator, moduleKey: "emi_calculator" },
        { path: "/mhm", label: "MyHomeMatrix", icon: BarChart, badge: "New", moduleKey: "mhm" },
      ],
    },
    {
      label: "Inventory",
      items: [
        { path: "/inventory", label: "Inventory", icon: Warehouse, moduleKey: "inventory" },
        { path: "/pricing", label: "Pricing Engine", icon: Receipt, moduleKey: "pricing" },
        { path: "/parking", label: "Parking", icon: ParkingCircle, moduleKey: "parking" },
      ],
    },
    {
      label: "Rental Desk",
      items: [
        { path: "/rentals", label: "Overview", icon: Building2, badge: "New", moduleKey: "rentals" },
        { path: "/rentals/units", label: "Units", icon: Home, moduleKey: "rentals" },
        { path: "/rentals/tenant-leads", label: "Tenant Leads", icon: Users, moduleKey: "rentals" },
        { path: "/rentals/visits", label: "Visits", icon: Calendar, moduleKey: "rentals" },
        { path: "/rentals/leases", label: "Leases", icon: KeyRound, moduleKey: "rentals" },
        { path: "/rentals/service-requests", label: "Service Requests", icon: Wrench, moduleKey: "rentals" },
      ],
    },
    {
      label: "Analytics",
      items: [
        { path: "/intelligence", label: "Intelligence", icon: Brain, moduleKey: "intelligence" },
        { path: "/analytics", label: "Analytics", icon: BarChart3, moduleKey: "analytics" },
        { path: "/reports", label: "AI Report Studio", icon: Sparkles, badge: "New" },
        { path: "/lost-analytics", label: "Lost Reasons", icon: TrendingDown, moduleKey: "lost_analytics" },
        { path: "/campaign-analytics", label: "Campaign Analytics", icon: TrendingUp, moduleKey: "campaign_roi" },
        { path: "/mhm-insights", label: "MhM Insights", icon: BarChart, moduleKey: "mhm_insights" },
        { path: "/control-tower", label: "Control Tower", icon: Radar, moduleKey: "control_tower" },
      ],
    },
    {
      label: "Operations",
      items: [
        { path: "/lead-capture", label: "Lead Capture", icon: Webhook, moduleKey: "lead_capture" },
        { path: "/campaigns", label: "Campaigns", icon: Megaphone, moduleKey: "campaigns" },
        { path: "/teams", label: "Teams", icon: UsersRound, moduleKey: "teams" },
        { path: "/workflows", label: "Workflows", icon: Workflow, moduleKey: "workflows" },
        { path: "/routing-rules", label: "Routing Rules", icon: GitBranch, moduleKey: "routing_rules" },
        { path: "/integration-health", label: "Integration Health", icon: Activity, moduleKey: "integration_health" },
        { path: "/automations", label: "Automations", icon: Zap, moduleKey: "automations" },
        { path: "/message-templates", label: "Message Templates", icon: MessageSquare, moduleKey: "message_templates" },
        { path: "/routing-logs", label: "Routing Logs", icon: ScrollText, moduleKey: "routing_logs" },
        { path: "/escalations", label: "Escalations", icon: AlertTriangle, moduleKey: "escalations" },
        { path: "/family-groups", label: "Decision Groups", icon: Heart, moduleKey: "family_groups" },
        { path: "/cp-submissions", label: "CP Portal", icon: Globe, moduleKey: "cp_portal" },
        { path: "/channel-partners", label: "Channel Partners", icon: Users, moduleKey: "channel_partners" },
      ],
    },
    {
      label: "Learning",
      items: [
        { path: "/training", label: "Training", icon: BookOpen, moduleKey: "training" },
        { path: "/training-admin", label: "Training Admin", icon: GraduationCap, moduleKey: "training_admin" },
      ],
    },
    {
      label: "Admin",
      items: [
        { path: "/form-builder", label: "Form Builder", icon: Settings2, moduleKey: "form_builder" },
        { path: "/data-health", label: "Data Health", icon: Activity, moduleKey: "data_health" },
        { path: "/data-import", label: "Data Import", icon: Upload, moduleKey: "data_import" },
        { path: "/sync-dashboard", label: "Sync Dashboard", icon: RefreshCw, moduleKey: "sync_dashboard" },
        { path: "/governance", label: "Governance", icon: ShieldCheck, badge: pendingCount > 0 ? `${pendingCount}` : null, moduleKey: "governance" },
        { path: "/audit", label: "Audit Trail", icon: Shield, moduleKey: "audit" },
        { path: "/settings", label: "Settings", icon: Settings, moduleKey: "settings" },
      ],
    },
    {
      label: "Support",
      items: [
        { path: "/help", label: "Help Center", icon: BookOpen, moduleKey: "help_center" },
      ],
    },
  ];

  // Filter nav items based on user permissions
  const navGroups = allNavGroups
    .map(group => ({
      ...group,
      items: group.items.filter(item => hasAccess(item.moduleKey)),
    }))
    .filter(group => group.items.length > 0);

  // Flat list for mobile bottom nav
  const navItems = navGroups.flatMap(g => g.items);

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  // NavLink and NavSection are defined outside Layout to prevent re-creation on renders

  // Close flyout only when clicking outside sidebar entirely
  React.useEffect(() => {
    if (!openGroup) return;
    const handler = (e) => {
      if (sidebarRef.current && !sidebarRef.current.contains(e.target)) {
        // Check if click is inside a flyout (fixed positioned, outside sidebar DOM)
        const flyout = document.querySelector('[data-flyout="true"]');
        if (flyout && flyout.contains(e.target)) return;
        setOpenGroup(null);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [openGroup]);

  // (NavSection moved outside Layout - see above)

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Desktop Sidebar */}
      <aside ref={sidebarRef} className="fixed left-0 top-0 h-full w-64 bg-slate-900 text-white hidden md:flex flex-col z-50">
        {/* Logo */}
        <div className="p-6 border-b border-white/10">
          <Link to="/dashboard" className="flex items-center gap-3">
            <div className="w-10 h-10 bg-amber-500 rounded-lg flex items-center justify-center">
              <Building2 className="w-6 h-6 text-white" />
            </div>
            <span className="text-xl font-bold font-['Manrope']">PropertyCRM</span>
          </Link>
        </div>

        {/* Project Selector - always show */}
        <div className="px-3 pt-3 pb-1">
          <select
            value={selectedProjectId}
            onChange={(e) => selectProject(e.target.value)}
            data-testid="global-project-selector"
            className="w-full bg-slate-800 text-white text-sm px-3 py-2 rounded-lg border border-white/10 focus:outline-none focus:ring-1 focus:ring-amber-500 cursor-pointer appearance-none"
            style={{ backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%23ffffff' stroke-width='2'%3E%3Cpath d='M6 9l6 6 6-6'/%3E%3C/svg%3E")`, backgroundRepeat: 'no-repeat', backgroundPosition: 'right 10px center' }}
          >
            <option value="all">All Projects</option>
            {projects.map(p => (
              <option key={p.project_id} value={p.project_id}>{p.name}</option>
            ))}
          </select>
        </div>

        {/* Navigation */}
        <nav className="flex-1 p-3 space-y-2 overflow-y-auto">
          {navGroups.map((group) => (
            <NavSection key={group.label} group={group} openGroup={openGroup} setOpenGroup={setOpenGroup} currentPath={location.pathname} />
          ))}
        </nav>

        {/* User Section */}
        <div className="p-4 border-t border-white/10">
          {/* Notification Bell */}
          <div className="relative mb-3" ref={bellRef}>
            <button
              onClick={(e) => { e.stopPropagation(); setBellOpen(!bellOpen); }}
              className="w-full flex items-center justify-between px-3 py-2 rounded-lg hover:bg-white/10 transition-colors cursor-pointer"
              data-testid="notification-bell"
              type="button"
            >
              <div className="flex items-center gap-3">
                <Bell className="w-5 h-5 text-slate-300" />
                <span className="text-sm text-slate-300">Notifications</span>
              </div>
              {unreadCount > 0 && (
                <span className="bg-amber-500 text-white text-xs font-medium px-2 py-0.5 rounded-full">
                  {unreadCount > 99 ? "99+" : unreadCount}
                </span>
              )}
            </button>

            {/* Notification Dropdown */}
            {bellOpen && (
              <div 
                className="fixed left-[260px] bottom-[80px] w-[340px] bg-white rounded-xl shadow-2xl border border-slate-200 overflow-hidden z-[9999]"
                style={{ maxHeight: 'calc(100vh - 120px)' }}
              >
                <div className="px-4 py-3 bg-slate-50 border-b border-slate-200 flex items-center justify-between">
                  <h3 className="text-sm font-semibold text-slate-800">Notifications</h3>
                  <div className="flex gap-1">
                    {unreadCount > 0 && (
                      <button
                        onClick={(e) => { e.stopPropagation(); markAllAsRead(); }}
                        className="text-xs text-blue-600 hover:text-blue-800 px-2 py-1 hover:bg-blue-50 rounded"
                        type="button"
                      >
                        Mark all read
                      </button>
                    )}
                    {notifications.length > 0 && (
                      <button
                        onClick={(e) => { e.stopPropagation(); clearAllNotifications(); }}
                        className="text-xs text-slate-500 hover:text-red-600 px-2 py-1 hover:bg-red-50 rounded"
                        type="button"
                      >
                        Clear all
                      </button>
                    )}
                  </div>
                </div>
                <div className="max-h-[400px] overflow-y-auto">
                  {notifications.length === 0 ? (
                    <div className="p-8 text-center text-slate-400">
                      <Bell className="w-10 h-10 mx-auto mb-2 opacity-30" />
                      <p className="text-sm">No notifications</p>
                    </div>
                  ) : (
                    notifications.map(notif => (
                      <div
                        key={notif.notification_id}
                        onClick={(e) => handleNotificationClick(e, notif)}
                        className={`px-4 py-3 border-b border-slate-100 hover:bg-slate-50 cursor-pointer transition-colors ${
                          !notif.is_read ? "bg-blue-50/50" : ""
                        }`}
                      >
                        <div className="flex gap-3">
                          <div className="mt-0.5">{getNotificationIcon(notif.category)}</div>
                          <div className="flex-1 min-w-0">
                            <p className={`text-sm ${!notif.is_read ? "font-medium text-slate-900" : "text-slate-700"}`}>
                              {notif.title}
                            </p>
                            <p className="text-xs text-slate-500 truncate mt-0.5">{notif.message}</p>
                            <p className="text-[10px] text-slate-400 mt-1">{formatTimeAgo(notif.created_at)}</p>
                          </div>
                          {!notif.is_read && (
                            <div className="w-2 h-2 bg-blue-500 rounded-full mt-2 shrink-0"></div>
                          )}
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>
            )}
          </div>

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button className="w-full flex items-center gap-3 px-3 py-2 rounded-lg hover:bg-white/10 transition-colors" data-testid="user-menu">
                <div className="w-8 h-8 rounded-full bg-slate-700 flex items-center justify-center overflow-hidden">
                  {user?.picture ? (
                    <img src={user.picture} alt={user.name} className="w-full h-full object-cover" />
                  ) : (
                    <span className="text-sm font-medium">{user?.name?.charAt(0)}</span>
                  )}
                </div>
                <div className="flex-1 text-left">
                  <p className="text-sm font-medium truncate">{user?.name}</p>
                  <p className="text-xs text-slate-400">{ROLE_LABELS[user?.role] || user?.role?.replace(/_/g, ' ')}</p>
                </div>
                <ChevronDown className="w-4 h-4 text-slate-400" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <DropdownMenuItem onClick={() => navigate('/settings')}>
                <Settings className="w-4 h-4 mr-2" />
                Settings
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={handleLogout} className="text-red-600" data-testid="logout-btn">
                <LogOut className="w-4 h-4 mr-2" />
                Logout
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </aside>

      {/* Mobile Header */}
      <header className="md:hidden fixed top-0 left-0 right-0 bg-white/90 backdrop-blur-md border-b border-slate-200 z-40 flex items-center justify-between px-4 h-14 pwa-safe-top safe-pl safe-pr">
        <Link to="/dashboard" className="flex items-center gap-2">
          <div className="w-8 h-8 bg-amber-500 rounded-lg flex items-center justify-center">
            <Building2 className="w-5 h-5 text-white" />
          </div>
          <span className="text-lg font-bold font-['Manrope'] text-slate-900">PropertyCRM</span>
        </Link>

        <div className="flex items-center gap-2">
          {/* Mobile Project Selector */}
          {projects.length > 0 && (
            <select
              value={selectedProjectId}
              onChange={(e) => selectProject(e.target.value)}
              className="bg-slate-100 text-slate-700 text-xs px-2 py-1.5 rounded-md border-0 focus:outline-none max-w-[120px]"
            >
              <option value="all">All Projects</option>
              {projects.map(p => (
                <option key={p.project_id} value={p.project_id}>{p.name}</option>
              ))}
            </select>
          )}
          {/* Mobile Notification Bell */}
          <button
            onClick={(e) => { e.stopPropagation(); setBellOpen(!bellOpen); }}
            className="relative p-2 rounded-lg hover:bg-slate-100 transition-colors"
            data-testid="mobile-notification-bell"
            type="button"
          >
            <Bell className="w-5 h-5 text-slate-600" />
            {unreadCount > 0 && (
              <span className="absolute -top-0.5 -right-0.5 bg-amber-500 text-white text-[10px] font-medium w-4 h-4 rounded-full flex items-center justify-center">
                {unreadCount > 9 ? "9+" : unreadCount}
              </span>
            )}
          </button>

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button className="flex items-center gap-2 px-2 py-1.5 rounded-lg hover:bg-slate-100 transition-colors" data-testid="mobile-user-menu">
                <div className="w-7 h-7 rounded-full bg-slate-200 flex items-center justify-center overflow-hidden">
                  {user?.picture ? (
                    <img src={user.picture} alt={user.name} className="w-full h-full object-cover" />
                  ) : (
                    <span className="text-xs font-medium">{user?.name?.charAt(0)}</span>
                  )}
                </div>
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-48">
              <div className="px-2 py-1.5">
                <p className="text-sm font-medium truncate">{user?.name}</p>
                <p className="text-xs text-slate-400">{ROLE_LABELS[user?.role] || user?.role?.replace(/_/g, ' ')}</p>
              </div>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={() => navigate('/settings')}>
                <Settings className="w-4 h-4 mr-2" />Settings
              </DropdownMenuItem>
              <DropdownMenuItem onClick={handleLogout} className="text-red-600" data-testid="mobile-logout-btn">
                <LogOut className="w-4 h-4 mr-2" />Logout
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </header>

      {/* Main Content */}
      <main className="md:pl-64 main-mobile-offset md:pt-0 min-h-screen pb-0 md:pb-0 safe-pl safe-pr safe-pb">
        {children}
      </main>

      {/* Mobile Bottom Nav */}
      <MobileBottomNav
        navItems={navItems}
        navGroups={navGroups}
        moreOpen={moreOpen}
        onMoreClick={() => setMoreOpen(true)}
        onMoreClose={() => setMoreOpen(false)}
      />

      {/* Mobile Notification Panel */}
      {bellOpen && (
        <div className="md:hidden fixed inset-0 z-[9999]" ref={mobilePanelRef}>
          {/* Backdrop */}
          <div 
            className="absolute inset-0 bg-black/50"
            onClick={() => setBellOpen(false)}
          />
          {/* Panel */}
          <div 
            className="absolute top-0 right-0 w-full max-w-sm h-full bg-white shadow-xl animate-slide-in-right"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between px-4 py-3 border-b border-slate-200 bg-slate-50">
              <h2 className="text-lg font-semibold text-slate-800">Notifications</h2>
              <div className="flex items-center gap-2">
                {unreadCount > 0 && (
                  <button
                    onClick={(e) => { e.stopPropagation(); markAllAsRead(); }}
                    className="text-xs text-blue-600 px-2 py-1 rounded hover:bg-blue-50"
                    type="button"
                  >
                    Mark all read
                  </button>
                )}
                <button
                  onClick={(e) => { e.stopPropagation(); setBellOpen(false); }}
                  className="p-1 hover:bg-slate-200 rounded"
                  type="button"
                >
                  <X className="w-5 h-5 text-slate-500" />
                </button>
              </div>
            </div>
            <div className="overflow-y-auto h-[calc(100%-60px)]">
              {notifications.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-64 text-slate-400">
                  <Bell className="w-12 h-12 mb-3 opacity-30" />
                  <p>No notifications</p>
                </div>
              ) : (
                <>
                  {notifications.map(notif => (
                    <div
                      key={notif.notification_id}
                      onClick={(e) => handleNotificationClick(e, notif)}
                      className={`px-4 py-3 border-b border-slate-100 active:bg-slate-100 ${
                        !notif.is_read ? "bg-blue-50/50" : ""
                      }`}
                    >
                      <div className="flex gap-3">
                        <div className="mt-0.5">{getNotificationIcon(notif.category)}</div>
                        <div className="flex-1 min-w-0">
                          <p className={`text-sm ${!notif.is_read ? "font-semibold text-slate-900" : "text-slate-700"}`}>
                            {notif.title}
                          </p>
                          <p className="text-xs text-slate-500 mt-0.5">{notif.message}</p>
                          <p className="text-[10px] text-slate-400 mt-1">{formatTimeAgo(notif.created_at)}</p>
                        </div>
                        {!notif.is_read && (
                          <div className="w-2 h-2 bg-blue-500 rounded-full mt-2 shrink-0"></div>
                        )}
                      </div>
                    </div>
                  ))}
                  {notifications.length > 0 && (
                    <div className="p-4">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          console.log("Clear all clicked");
                          clearAllNotifications();
                        }}
                        className="w-full py-2 text-sm text-red-600 hover:bg-red-50 rounded-lg border border-red-200"
                        data-testid="clear-all-notifications-btn"
                        type="button"
                      >
                        Clear All Notifications
                      </button>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* PWA Install Prompt */}
      <InstallPrompt />
    </div>
  );
};

export default Layout;
